import { AveragesParams } from '@data/Simulations/pipeline/blocks/AveragesBlock';
import '@data/Simulations/pipeline/blocks/AveragesBlock';
import { StageExecutionMode } from '@data/Simulations/pipeline/Stage';
import { AveragesType } from '@data/Simulations/pipeline/stages/transformations/AveragesStage';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { getSentence } from '@gui/constants/sentences';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { Box, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import type { BlockParameters } from '@visualizer/types';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { usePipelineBlockParams } from './usePipelineBlockParams';

type AveragesParametersProps = {
    blockId: string;
};

export const getAveragesDefaultParams = (): Pick<AveragesParams, 'averagesType' | 'executionMode'> => ({
    averagesType: AveragesType.AVERAGE,
    executionMode: StageExecutionMode.ALL,
});

const AveragesParameters: FC<AveragesParametersProps> = ({ blockId }) => {
    const params = usePipelineBlockParams<AveragesParams>(blockId);
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);
    const defaultParams = useMemo(() => getAveragesDefaultParams(), []);
    const updateParams = (partial: Partial<AveragesParams>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };

    useEffect(() => {
        if (!params) {
            updateParams(defaultParams);
            return;
        }

        const missing: Partial<AveragesParams> = {};
        if (params.averagesType === undefined) {
            missing.averagesType = defaultParams.averagesType;
        }
        if (params.executionMode === undefined) {
            missing.executionMode = defaultParams.executionMode;
        }

        if (Object.keys(missing).length > 0) {
            updateParams(missing);
        }
    }, [params, updateParams, defaultParams]);

    return (
        <Box>
            <RadioGroup
                row
                className="panel-compact-control-stack"
                sx={{ gap: 0.5 }}
                value={params?.executionMode}
                onChange={(_e, v) => updateParams({ executionMode: v as StageExecutionMode })}
            >
                <FormControlLabel value={StageExecutionMode.ALL} control={<Radio size="small" />} label={'Global'} />
                <FormControlLabel value={StageExecutionMode.BY_FLOOR} control={<Radio size="small" />} label={'Horizontal'} />
                <FormControlLabel value={StageExecutionMode.BY_COLUMN} control={<Radio size="small" />} label={'Vertical'} />
            </RadioGroup>

            <RadioGroup
                row
                className="panel-compact-control-stack"
                sx={{ gap: 0.5, mb: 1.1, mt: 0.5 }}
                value={params?.averagesType}
                onChange={(_e, v) => updateParams({ averagesType: Number(v) as AveragesType })}
            >
                <FormControlLabel
                    value={AveragesType.AVERAGE}
                    control={<Radio size="small" />}
                    label={
                        <CustomTooltip side="right" mode="icon" title={getSentence('Pipeline.Filters.Averages.Average.Tooltip')}>
                            <span>Average</span>
                        </CustomTooltip>
                    }
                />
                <FormControlLabel
                    value={AveragesType.MEDIAN}
                    control={<Radio size="small" />}
                    label={
                        <CustomTooltip side="right" mode="icon" title={getSentence('Pipeline.Filters.Averages.Median.Tooltip')}>
                            <span>Median</span>
                        </CustomTooltip>
                    }
                />
            </RadioGroup>
        </Box>
    );
};

export default AveragesParameters;
